import { Select, Table, TableColumnsType } from 'antd';
import { FC, useEffect, useState } from 'react';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import { getSettledSemesterCourses } from '../../../api/semesterCourses.api';
import {
  IGetSettledSemesterCourses,
  ISettledSemesterCourse,
} from '../../../models/semesterCourses.model';
import SelectSemesterCourseSubject from '../../SelectSemesterCourseSubject';
import {
  IGetSemesterLessonPlans,
  IPutSemesterLessonPlansReq,
  ISemesterLessonPlan,
} from '../../../models/semesterLessonPlan2.model';
import {
  getSemesterLessonPlans,
  putSemesterLessonPlans,
} from '../../../api/semesterLessonPlan2.api';
import { useTranslation } from 'react-i18next';

interface IProps {
  semesterCode: string;
  submitTrigger: boolean;
}

const SemesterPlan: FC<IProps> = ({ semesterCode, submitTrigger }) => {
  const { t } = useTranslation();
  const { successNotification } = useNotification();

  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetSettledSemesterCourses>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetSemesterLessonPlans>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [semesterCourseCode, setSemesterCourseCode] = useState<number>();
  const [subjectCode, setSubjectCode] = useState<number>();
  const [lessonNames, setLessonNames] = useState<string[]>([]);
  const [data, setData] = useState<ISemesterLessonPlan[]>([]);

  useEffect(() => {
    if (semesterCode) {
      execute(getSettledSemesterCourses({ semesterCode }));
      setSubjectCode(undefined);
    }
  }, [semesterCode]);

  useEffect(() => {
    if (semesterCourseCode && subjectCode) {
      execute2(
        getSemesterLessonPlans({
          semesterCourseCode,
          subjectCode,
        }),
      );
    }
  }, [semesterCourseCode, subjectCode]);

  useEffect(() => {
    if (res2?.data?.length) {
      setData(res2.data);
      setLessonNames(res2.data.map((item) => item.lessonName || ''));
    } else {
      setData([]);
      setLessonNames([]);
    }
  }, [res2]);

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }
    onSubmit();
  }, [submitTrigger]);

  useEffect(() => {
    if (submitRes) {
      successNotification(t('common.message.save1'));
    }
  }, [submitRes]);

  const onSetLessonNames = (index: number, value: string) => {
    const newLessonNames = [...lessonNames];
    newLessonNames[index] = value;
    setLessonNames(newLessonNames);
  };

  const onSubmit = () => {
    if (!res2) return;
    const req: IPutSemesterLessonPlansReq[] = res2.data.map((item, index) => ({
      lessonPlanCode: item.lessonPlanCode,
      lessonName: lessonNames[index],
    }));
    submitExecute(putSemesterLessonPlans(req));
  };

  const columns: TableColumnsType<ISemesterLessonPlan> = [
    {
      title: t('program.semester.4'),
      dataIndex: 'subjectSeq',
      align: 'center',
      width: 100,
    },
    {
      title: 'Lesson',
      dataIndex: 'lessonPlanCode',
      render: (value, record, index) => (
        <input
          type="text"
          className="form-control"
          value={lessonNames[index]}
          onChange={(e) => onSetLessonNames(index, e.target.value)}
        />
      ),
    },
  ];

  return (
    <>
      {(isLoading || isLoading2 || submitIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={submitError} />

      <div className="comm-bx__space pt-0">
        <div className="comm-btn__area right">
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('program.semester.11')}
              value={semesterCourseCode}
              onChange={(value) => {
                setSubjectCode(undefined);
                setData([]);
                setSemesterCourseCode(value);
              }}
            >
              {res?.data?.map((item: ISettledSemesterCourse) => (
                <Select.Option
                  key={item.semesterCourseCode}
                  value={item.semesterCourseCode}
                >
                  {item.courseDetailName}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="w-150p">
            <SelectSemesterCourseSubject
              semesterCourseCode={semesterCourseCode}
              subjectCode={subjectCode}
              setSubjectCode={setSubjectCode}
            />
          </div>
        </div>
      </div>

      <Table
        className='comm-table__area'
        columns={columns}
        dataSource={data}
        pagination={{ pageSize: 99999 }}
        rowKey="lessonPlanCode"
      />
    </>
  );
};

export default SemesterPlan;
